import { Box, Flex, Text, Heading, Badge } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  AppButton,
  AppTable,
  TableRow,
  TableCell,
  AppModal,
  AppInput,
  AppSelect,
} from "../../components";
import { colors } from "../../theme";
import {
  PlusIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  MallIcon,
} from "../../utils/svg";
import { Controller, useForm } from "react-hook-form";
import {
  useAllCountries,
  useCitiesByCountry,
  useCreateMall,
  useMalls,
  useUpdateMall,
  useDeleteMall,
} from "../../services/useQueries";
import { useDispatch } from "react-redux";
import { setLoading } from "../../redux/reducer/utilsSlice";
import { getCityById } from "../../services/apiCalls";

const MallManagementScreen = () => {
  // Two independent forms so the page-header filter (country/city above the
  // table) and the create/edit modal don't share state. They used to share
  // a single useForm() — opening the edit modal would `reset()` country/city
  // for the mall being edited, which silently filtered the table behind it.
  const {
    control: filterControl,
    watch: filterWatch,
    setValue: filterSetValue,
  } = useForm();

  const {
    control: modalControl,
    register: modalRegister,
    handleSubmit: modalHandleSubmit,
    reset: modalReset,
    watch: modalWatch,
    setValue: modalSetValue,
    formState: { errors: modalErrors },
  } = useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMall, setSelectedMall] = useState(null);
  const [page, setPage] = useState(1);
  const { data: countries } = useAllCountries();
  // The filter drives the table; the modal drives the create/edit payload.
  // They each look up their own cities list from their own selected country.
  const filterCountryId = filterWatch("country");
  const filterCityId = filterWatch("city");
  const modalCountryId = modalWatch("country");
  const { data: filterCities, isLoading: filterCitiesLoading } =
    useCitiesByCountry(filterCountryId);
  const { data: modalCities, isLoading: modalCitiesLoading } =
    useCitiesByCountry(modalCountryId);
  // Aliases kept so the rest of the screen reads naturally.
  const selectedCityId = filterCityId;
  const cities = modalCities;
  const citiesLoading = modalCitiesLoading;
  // Fetch malls by whichever filter is set. Backend resolves precedence:
  // cityId is more specific than countryId; if both, both apply.
  const { data: mallsData, isLoading: mallsLoading } = useMalls(
    filterCityId || filterCountryId
      ? { cityId: filterCityId, countryId: filterCountryId }
      : undefined
  );
  const malls = mallsData || [];
  // Mall delete mutation
  const deleteMallMutation = useDeleteMall();
  // Mall update mutation
  const updateMallMutation = useUpdateMall();

  // Set global loading state based on mallsLoading or citiesLoading

  const dispatch = useDispatch();

  // Mall create mutation
  const createMallMutation = useCreateMall();

  const headers = ["Mall Name", "Location", "Shops", "Actions"];

  const handleAddMall = () => {
    setSelectedMall(null);
    // Pre-fill country/city from the table filter so adding within the
    // filtered view doesn't require re-picking the location.
    modalReset({
      name: "",
      location: "",
      country: filterCountryId || "",
      city: filterCityId || "",
    });
    setIsModalOpen(true);
  };

  const handleEditMall = async (mall) => {
    setSelectedMall(mall);

    // The malls API returns rows shaped as
    //   { id, name, address, cityId, restaurantCount }
    // but the form fields are named
    //   name, location, city, country, restaurants, description
    // Map the API shape onto the form shape, and look up the parent country
    // since the mall payload doesn't include it.
    let countryId = "";
    if (mall.cityId) {
      try {
        const city = await getCityById(mall.cityId);
        countryId = city?.countryId || "";
      } catch (e) {
        console.warn("[MallEdit] could not derive country from city", mall.cityId, e?.message || e);
      }
    }

    modalReset({
      name: mall.name || "",
      location: mall.address || "",
      city: mall.cityId || "",
      country: countryId,
    });
    setIsModalOpen(true);
  };

  const handleDeleteMall = (mall) => {
    if (!mall.id) return;
    deleteMallMutation.mutate(mall.id);
  };

  const onSubmit = (data) => {
    const payload = {
      name: data.name,
      address: data.location,
      cityId: data.city,
      // Optionally add more fields if API supports
    };
    if (selectedMall && selectedMall.id) {
      updateMallMutation.mutate(
        { id: selectedMall.id, data: payload },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            modalReset({});
          },
        }
      );
    } else {
      createMallMutation.mutate(payload, {
        onSuccess: () => {
          setIsModalOpen(false);
          modalReset({});
        },
      });
    }
  };

  const renderRow = (mall, index) => (
    <TableRow key={mall.id}>
      <TableCell fontWeight="500">{mall.name}</TableCell>
      <TableCell>{mall.address}</TableCell>

      <TableCell>{mall.restaurantCount ?? 0}</TableCell>

      <TableCell>
        <Flex gap={2}>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => {}}
            leftIcon={<ViewIcon boxSize={4} />}
          />
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleEditMall(mall)}
            leftIcon={<EditIcon boxSize={4} />}
          />
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteMall(mall)}
            leftIcon={<DeleteIcon boxSize={4} color="#EF4444" />}
          />
        </Flex>
      </TableCell>
    </TableRow>
  );
  useEffect(() => {
    dispatch(setLoading(mallsLoading || citiesLoading));
  }, [mallsLoading, citiesLoading, dispatch]);
  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Mall Management
          </Heading>
          <Text color={colors.textSecondary}>
            Manage all malls in the system
          </Text>
        </Box>
        <Flex gap={3}>
          {/* Filter row — bound to the filter form, independent of the modal */}
          <Controller
            control={filterControl}
            name="country"
            render={({ field }) => (
              <AppSelect
                placeholder="Filter by Country"
                options={
                  countries?.map((c) => ({ value: c.id, label: c.name })) || []
                }
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  filterSetValue("city", undefined); // reset dependent filter
                }}
                width="200px"
              />
            )}
          />
          <Controller
            control={filterControl}
            name="city"
            render={({ field }) => (
              <AppSelect
                placeholder={
                  filterCitiesLoading ? "Loading cities..." : "Filter by City"
                }
                options={
                  filterCities?.map((c) => ({ value: c.id, label: c.name })) || []
                }
                value={field.value}
                onChange={field.onChange}
                disabled={!filterCountryId || filterCitiesLoading}
                width="200px"
              />
            )}
          />

          {/* Clear Filter — only renders when at least one filter is set */}
          {(filterCountryId || filterCityId) && (
            <AppButton
              variant="secondary"
              onClick={() => {
                filterSetValue("country", undefined);
                filterSetValue("city", undefined);
              }}
            >
              Clear Filter
            </AppButton>
          )}

          <AppButton
            variant="primary"
            leftIcon={<MallIcon boxSize={5} />}
            onClick={handleAddMall}
          >
            Add Mall
          </AppButton>
        </Flex>
      </Flex>

      {/* Table */}
      <AppTable
        headers={headers}
        data={malls}
        renderRow={renderRow}
        loading={mallsLoading}
        pagination={{
          page,
          totalPages: 1, // Update with real pagination if available
          onPageChange: setPage,
        }}
      />

      {/* Add/Edit Modal */}
      <AppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedMall ? "Edit Mall" : "Add New Mall"}
        size="lg"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton variant="primary" onClick={modalHandleSubmit(onSubmit)}>
              {selectedMall ? "Update Mall" : "Add Mall"}
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <AppInput
            label="Mall Name"
            name="name"
            placeholder="Enter mall name"
            register={modalRegister}
            errors={modalErrors}
            required
          />
          <Controller
            control={modalControl}
            name="country"
            rules={{ required: true }}
            render={({ field }) => (
              <AppSelect
                label="Country"
                placeholder="Select country"
                options={
                  countries?.map((c) => ({ value: c.id, label: c.name })) || []
                }
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  modalSetValue("city", undefined);
                }}
                error={modalErrors.country}
              />
            )}
          />

          <Controller
            control={modalControl}
            name="city"
            rules={{ required: true }}
            render={({ field }) => (
              <AppSelect
                label="City"
                placeholder="Select city"
                options={
                  cities?.map((c) => ({ value: c.id, label: c.name })) || []
                }
                value={field.value}
                onChange={field.onChange}
                disabled={!modalCountryId}
                error={modalErrors.city}
              />
            )}
          />

          <AppInput
            label="Location"
            name="location"
            placeholder="Enter location"
            register={modalRegister}
            errors={modalErrors}
            required
          />

        </Flex>
      </AppModal>
    </Box>
  );
};

export default MallManagementScreen;
